import { yupResolver } from '@hookform/resolvers/yup';
import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import clsx from 'clsx';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import MultiSelectDropdown from 'components/common/MultiSelectDropdown';
import TimeRangeControl from 'components/formfields/TimeRangeControl';
import WeekDayMaskControl from 'components/formfields/WeekDayMaskControl';
import { selectIsEditMode } from 'features/common/editModeSlice';
import displayTimingGroupService from 'features/DisplayTimingGroup/displayTimingGroupService';
import { DisplayTimingGroupAddEditPayload, DisplayTimingGroupDetailItem } from 'features/DisplayTimingGroup/models';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DATE_HYPHEN_FORMAT, displayError, handleServiceError } from 'utils';
import * as yup from 'yup';
import {
  CONDITION_EXCLUDE,
  CONDITION_INCLUDE,
  CONDITION_SOURCE,
  GIVEN_EXTEND_TIME_DEFAULT,
  GIVEN_EXTEND_TIME_OPTIONS,
  GIVEN_TIME_TYPE_LUNCH,
  GIVEN_TIME_TYPE_NONE,
  GIVEN_TIME_TYPE_RANGE,
  MONTHLY_DAY_OPTIONS,
  WEEKDAY_MASK_DEFAULT,
} from '../../constants';
import './style.scss';

const TITLE_COL_WIDTH = '24%';
const PERIOD_PICKER_WIDTH = '111px';

// form stores dates as YYYY-MM-DD strings
function toDate(value?: string | null): Date | null {
  if (!value) return null;
  const m = moment(value, DATE_HYPHEN_FORMAT, true);
  return m.isValid() ? m.toDate() : null;
}

function toDateString(date: Date | null): string {
  return date ? moment(date).format(DATE_HYPHEN_FORMAT) : '';
}

interface DisplayTimingGroupDetailModalProps {
  displayTimingGroupId?: number;
  copyFromId?: number;
  onClose?: (isDataChanged: boolean) => void;
}

interface FormValues {
  group_name: string;
  note: string;
  is_active: boolean;

  weekday_mask: number;
  weekday_condition: 'include' | 'exclude';

  period_enabled: boolean;
  period_start: string;
  period_end: string;
  period_condition: 'include' | 'exclude';

  monthly_day_enabled: boolean;
  monthly_days: number[];
  monthly_day_condition: 'include' | 'exclude';

  given_time_type: 'none' | 'lunch' | 'range';
  given_lunch_condition: 'include' | 'exclude';
  given_lunch_extend_time: number;
  given_range_date: {
    given_range_start: string;
    given_range_end: string;
  };
  given_range_condition: 'include' | 'exclude';
  given_range_extend_time: number;

  overlay_enabled: boolean;
  overlay_message_ja: string;
  overlay_message_en: string;
  overlay_message_zh_hans: string;
  overlay_message_zh_hant: string;
  overlay_message_ko: string;
  overlay_message_vi: string;
}

function DisplayTimingGroupDetailModal({
  displayTimingGroupId,
  copyFromId,
  onClose,
}: DisplayTimingGroupDetailModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // refs

  // states
  const [data, setData] = useState<DisplayTimingGroupDetailItem | null>(null);

  // yup setup
  const schema = yup.object({
    group_name: yup
      .string()
      .required(t('DisplayTimingGroup.warn_field_required'))
      .max(100, t('DisplayTimingGroup.warn_max_length', { max: 100 })),
    note: yup.string().max(500, t('DisplayTimingGroup.warn_max_length', { max: 500 })),

    period_start: yup.string().when('period_enabled', {
      is: true,
      then: (s) => s.required(t('DisplayTimingGroup.warn_period_start_required')),
    }),
    period_end: yup.string().when('period_enabled', {
      is: true,
      then: (s) =>
        s
          .required(t('DisplayTimingGroup.warn_period_end_required'))
          .test('period-order', t('DisplayTimingGroup.warn_period_order'), function (value) {
            const { period_start } = this.parent;
            if (!period_start || !value) return true;
            // both values are in YYYY-MM-DD format, so string comparison works
            return period_start <= value;
          }),
    }),

    monthly_days: yup.array().when('monthly_day_enabled', {
      is: true,
      then: (s) => s.min(1, t('DisplayTimingGroup.warn_monthly_days_required')),
    }),

    given_range_date: yup.object().when('given_time_type', {
      is: GIVEN_TIME_TYPE_RANGE,
      then: (s) =>
        s.shape({
          given_range_start: yup.string().required(t('DisplayTimingGroup.warn_given_range_start_required')),
          given_range_end: yup
            .string()
            .required(t('DisplayTimingGroup.warn_given_range_end_required'))
            .test('given-range-order', t('DisplayTimingGroup.warn_given_range_order'), function (value) {
              const { given_range_start } = this.parent;
              if (!given_range_start || !value) return true;
              if (given_range_start === 'Open' || value === 'Close') return true;
              return given_range_start <= value;
            }),
        }),
    }),

    overlay_message_ja: yup.string().when('overlay_enabled', {
      is: true,
      then: (s) => s.required(t('DisplayTimingGroup.warn_field_required')),
    }),
  });

  // forms
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      group_name: '',
      note: '',
      is_active: true,

      weekday_mask: WEEKDAY_MASK_DEFAULT,
      weekday_condition: CONDITION_INCLUDE,

      period_enabled: false,
      period_start: '',
      period_end: '',
      period_condition: CONDITION_INCLUDE,

      monthly_day_enabled: false,
      monthly_days: [],
      monthly_day_condition: CONDITION_INCLUDE,

      given_time_type: GIVEN_TIME_TYPE_NONE,
      given_lunch_condition: CONDITION_INCLUDE,
      given_lunch_extend_time: GIVEN_EXTEND_TIME_DEFAULT,
      given_range_date: {
        given_range_start: '',
        given_range_end: '',
      },
      given_range_condition: CONDITION_INCLUDE,
      given_range_extend_time: GIVEN_EXTEND_TIME_DEFAULT,

      overlay_enabled: false,
      overlay_message_ja: '',
      overlay_message_en: '',
      overlay_message_zh_hans: '',
      overlay_message_zh_hant: '',
      overlay_message_ko: '',
      overlay_message_vi: '',
    },
  });

  // watches
  const periodEnabled = watch('period_enabled');
  const periodStart = watch('period_start');
  const monthlyDayEnabled = watch('monthly_day_enabled');
  const givenLunchCondition = watch('given_lunch_condition');
  const givenRangeCondition = watch('given_range_condition');
  const overlayEnabled = watch('overlay_enabled');

  // functions
  function loadData() {
    (async () => {
      try {
        const idToLoad = displayTimingGroupId ?? copyFromId;
        if (idToLoad) {
          const response = await displayTimingGroupService.getItem(idToLoad);
          const data = response.data;
          if (displayTimingGroupId) {
            setData(data);
          }
          if (copyFromId) {
            data.group_name = `${data.group_name}（コピー）`;
          }
          reset({
            group_name: data.group_name,
            note: data.note ?? '',
            is_active: data.is_active,

            weekday_mask: data.weekday_mask,
            weekday_condition: data.weekday_condition,

            period_enabled: data.period_enabled,
            period_start: data.period_start ?? '',
            period_end: data.period_end ?? '',
            period_condition: data.period_condition,

            monthly_day_enabled: data.monthly_day_enabled,
            monthly_days: data.monthly_days,
            monthly_day_condition: data.monthly_day_condition,

            given_time_type: data.given_time_type,
            given_lunch_condition: data.given_lunch_condition,
            given_lunch_extend_time: data.given_lunch_extend_time,
            given_range_date: {
              given_range_start: data.given_range_start ?? '',
              given_range_end: data.given_range_end ?? '',
            },
            given_range_condition: data.given_range_condition,
            given_range_extend_time: data.given_range_extend_time,

            overlay_enabled: data.overlay_enabled,
            overlay_message_ja: data.overlay_message_ja ?? '',
            overlay_message_en: data.overlay_message_en ?? '',
            overlay_message_zh_hans: data.overlay_message_zh_hans ?? '',
            overlay_message_zh_hant: data.overlay_message_zh_hant ?? '',
            overlay_message_ko: data.overlay_message_ko ?? '',
            overlay_message_vi: data.overlay_message_vi ?? '',
          });
        }
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  // handlers
  function handleClose(isDataChanged: boolean = false) {
    onClose && onClose(isDataChanged);
  }

  async function handleSave(formValue: FormValues) {
    // compose payload
    const payload: DisplayTimingGroupAddEditPayload = {
      group_name: formValue.group_name,
      note: formValue.note,
      is_active: formValue.is_active,

      weekday_mask: formValue.weekday_mask,
      weekday_condition: formValue.weekday_condition,

      period_enabled: formValue.period_enabled,
      period_start: formValue.period_enabled ? formValue.period_start : null,
      period_end: formValue.period_enabled ? formValue.period_end : null,
      period_condition: formValue.period_condition,

      monthly_day_enabled: formValue.monthly_day_enabled,
      monthly_days: formValue.monthly_day_enabled ? formValue.monthly_days : [],
      monthly_day_condition: formValue.monthly_day_condition,

      given_time_type: formValue.given_time_type,
      given_lunch_condition: formValue.given_lunch_condition,
      given_lunch_extend_time: formValue.given_lunch_extend_time,
      given_range_start:
        formValue.given_time_type === GIVEN_TIME_TYPE_RANGE ? formValue.given_range_date.given_range_start : null,
      given_range_end:
        formValue.given_time_type === GIVEN_TIME_TYPE_RANGE ? formValue.given_range_date.given_range_end : null,
      given_range_condition: formValue.given_range_condition,
      given_range_extend_time: formValue.given_range_extend_time,

      overlay_enabled: formValue.overlay_enabled,
      overlay_message_ja: formValue.overlay_message_ja,
      overlay_message_en: formValue.overlay_message_en,
      overlay_message_zh_hans: formValue.overlay_message_zh_hans,
      overlay_message_zh_hant: formValue.overlay_message_zh_hant,
      overlay_message_ko: formValue.overlay_message_ko,
      overlay_message_vi: formValue.overlay_message_vi,
    };

    try {
      const response = displayTimingGroupId
        ? await displayTimingGroupService.update(displayTimingGroupId, payload)
        : await displayTimingGroupService.add(payload);
      showGlobalModal(() => (
        <AlertModal title={t('App.info')} message={response.message} onClose={() => handleClose(true)} />
      ));
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  function onSubmit(formValue: FormValues) {
    // check formValue.overlay_enabled != data.overlay_enabled
    if (data && formValue.overlay_enabled !== data.overlay_enabled) {
      // show confirmation modal
      showGlobalModal(() => (
        <ConfirmModal
          title={t('App.confirm')}
          message={t('DisplayTimingGroup.confirm_overlay_enabled')}
          onConfirm={() => {
            handleSave(formValue);
          }}
        />
      ));
      return;
    }

    handleSave(formValue);
  }

  function processDelete(idsToDelete: number[]) {
    (async () => {
      try {
        const response = await displayTimingGroupService.delete(idsToDelete);
        showGlobalModal(() => (
          <AlertModal title={t('App.info')} message={response.message} onClose={handleClose.bind(null, true)} />
        ));
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function handleDelete() {
    if (!displayTimingGroupId) return;

    const idsToDelete = [displayTimingGroupId];
    // call API to get belong menus
    (async () => {
      try {
        const response = await displayTimingGroupService.getBelongMenus(idsToDelete);
        if (response.data) {
          const groupCheckInfo = response.data[0];
          if (groupCheckInfo.menu_count > 0) {
            showGlobalModal(() => (
              <AlertModal
                title={t('App.confirm')}
                message={t('DisplayTimingGroup.confirm_delete_group', { menu_count: groupCheckInfo.menu_count })}
              />
            ));
          } else {
            showGlobalModal(() => (
              <ConfirmModal
                title={t('App.confirm')}
                message={t('DisplayTimingGroup.confirm_delete')}
                onConfirm={() => {
                  processDelete(idsToDelete);
                }}
              />
            ));
          }
        }
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function getHeadTitle() {
    if (copyFromId) return t('DisplayTimingGroup.copy_head_title');
    if (displayTimingGroupId) return t('DisplayTimingGroup.detail_head_title');
    return t('DisplayTimingGroup.add_head_title');
  }

  function renderUpdatedInfo() {
    if (!data) return null;

    return (
      <span>
        最終更新：{moment(data.updated_at).format('YYYY/MM/DD')} {data.updated_by}
      </span>
    );
  }

  // side effects
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayTimingGroupId, copyFromId]);

  return (
    <Modal
      size="lg"
      dialogClassName="display-timing-group-detail-modal"
      show
      backdrop="static"
      onHide={handleClose}
      centered
    >
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={getHeadTitle()}></FeatureTitleBar>
      </Modal.Header>
      <Modal.Body className={clsx('pt-0', isEditMode ? 'reedit-mode-wrapper' : '')}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={(e) => {
            // prevent submitting form by pressing Enter in input fields
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
              e.preventDefault();
            }
          }}
        >
          <Row className="align-items-center display-timing-group-detail-modal__sticky-actions">
            <Col sm={3}>{renderUpdatedInfo()}</Col>
            <Col sm={9} className="d-flex justify-content-end">
              {displayTimingGroupId && (
                <Button className="btn--red" onClick={handleDelete}>
                  グループを削除
                </Button>
              )}
              <Button type="submit" className="btn--dark-green ms-3" disabled={isSubmitting}>
                {t('DisplayTimingGroup.save')}
              </Button>
            </Col>
          </Row>
          <Table bordered className="form-menu-book">
            <tbody>
              <tr>
                <th colSpan={2} className="text-center" style={{ width: TITLE_COL_WIDTH }}>
                  {t('DisplayTimingGroup.group_name')}
                  <span className="text-danger">&nbsp;(*)</span>
                </th>
                <td>
                  <Form.Control {...register('group_name')}></Form.Control>
                  {errors?.group_name && (
                    <span className="small text-danger d-inline-block mt-1">{errors.group_name.message}</span>
                  )}
                  <div className="mt-3 small">※ メニュー詳細画面のプルダウンに表示される名称</div>
                </td>
              </tr>
              <tr>
                <th colSpan={2} className="text-center">
                  {t('DisplayTimingGroup.note')}
                </th>
                <td>
                  <Form.Control {...register('note')}></Form.Control>
                  {errors?.note && <span className="small text-danger d-inline-block mt-1">{errors.note.message}</span>}
                </td>
              </tr>
              <tr>
                <th colSpan={2} className="text-center">
                  {t('DisplayTimingGroup.status')}
                </th>
                <td>
                  <Controller
                    control={control}
                    name="is_active"
                    render={({ field }) => (
                      <>
                        <Form.Check
                          inline
                          type="radio"
                          id="is_active_true"
                          label="有効"
                          checked={field.value === true}
                          onChange={() => field.onChange(true)}
                        />
                        <Form.Check
                          inline
                          type="radio"
                          id="is_active_false"
                          label="無効"
                          checked={field.value === false}
                          onChange={() => field.onChange(false)}
                        />
                      </>
                    )}
                  />
                </td>
              </tr>
              <tr>
                <th colSpan={2} className="text-center align-top">
                  {t('DisplayTimingGroup.condition')}
                </th>
                <td>
                  <div className="display-timing-group-box mb-3">
                    <div className="header">
                      <span className="icon bg-primary text-white">1</span>
                      <span>{t('DisplayTimingGroup.week_control')}</span>
                    </div>
                    <div className="body">
                      <div className="d-flex gap-3 align-items-center">
                        <WeekDayMaskControl control={control} name="weekday_mask" />
                        <Form.Select
                          className="text-center"
                          {...register('weekday_condition')}
                          style={{ width: '100px' }}
                        >
                          {CONDITION_SOURCE.map(({ value, labelKey }) => (
                            <option key={value} value={value}>
                              {t(labelKey)}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                      <div className="mt-3 small">
                        ※ デフォルトは全曜日ON（絞り込みなし）。祝日判定は日本祝日カレンダーに準拠。
                      </div>
                    </div>
                  </div>

                  <div className="display-timing-group-box mb-3">
                    <div className="header">
                      <span className="icon bg-primary text-white">2</span>
                      <span>{t('DisplayTimingGroup.date_control')}</span>
                    </div>
                    <div className="body">
                      <div className="d-flex gap-3 align-items-center">
                        <Form.Check
                          type="checkbox"
                          id="period_enabled"
                          label={t('DisplayTimingGroup.period_control')}
                          {...register('period_enabled')}
                          style={{ width: '130px' }}
                        />
                        <div className="d-flex gap-2 align-items-center">
                          <div style={{ width: PERIOD_PICKER_WIDTH }}>
                            <Controller
                              control={control}
                              name="period_start"
                              render={({ field }) => (
                                <ReactDatePicker
                                  className="form-control"
                                  locale="ja"
                                  dateFormat="yyyy/MM/dd"
                                  placeholderText="yyyy/mm/dd"
                                  strictParsing
                                  showYearDropdown
                                  showMonthDropdown
                                  dropdownMode="select"
                                  isClearable={periodEnabled}
                                  disabled={!periodEnabled}
                                  selected={toDate(field.value)}
                                  onChange={(date: Date | null) => {
                                    const newValue = toDateString(date);
                                    if (newValue === field.value) return;
                                    field.onChange(newValue);
                                    // period_end must be re-selected whenever period_start changes
                                    setValue('period_end', '');
                                  }}
                                  onBlur={field.onBlur}
                                />
                              )}
                            />
                          </div>
                          <span>~</span>
                          <div style={{ width: PERIOD_PICKER_WIDTH }}>
                            <Controller
                              control={control}
                              name="period_end"
                              render={({ field }) => (
                                <ReactDatePicker
                                  className="form-control"
                                  locale="ja"
                                  dateFormat="yyyy/MM/dd"
                                  placeholderText="yyyy/mm/dd"
                                  strictParsing
                                  showYearDropdown
                                  showMonthDropdown
                                  dropdownMode="select"
                                  isClearable={periodEnabled}
                                  disabled={!periodEnabled}
                                  selected={toDate(field.value)}
                                  minDate={toDate(periodStart)}
                                  onChange={(date: Date | null) => field.onChange(toDateString(date))}
                                  onBlur={field.onBlur}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <Form.Select
                          className="text-center"
                          {...register('period_condition')}
                          disabled={!periodEnabled}
                          style={{ width: '100px' }}
                        >
                          {CONDITION_SOURCE.map(({ value, labelKey }) => (
                            <option key={value} value={value}>
                              {t(labelKey)}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                      {periodEnabled && (errors?.period_start || errors?.period_end) && (
                        // mirror the picker layout above so each message sits under its own control
                        <div className="d-flex gap-2 mt-1" style={{ marginLeft: '146px' }}>
                          <span className="small text-danger flex-shrink-0" style={{ width: PERIOD_PICKER_WIDTH }}>
                            {errors?.period_start?.message}
                          </span>
                          <span className="invisible">〜</span>
                          <span className="small text-danger">{errors?.period_end?.message}</span>
                        </div>
                      )}
                      <div className="d-flex gap-3 align-items-center mt-3">
                        <Form.Check
                          type="checkbox"
                          id="monthly_day_enabled"
                          label={t('DisplayTimingGroup.monthly_day_control')}
                          {...register('monthly_day_enabled')}
                          style={{ width: '130px' }}
                        />
                        <span style={{ width: '30px' }}>毎月</span>
                        <div style={{ width: '180px' }}>
                          <Controller
                            control={control}
                            name="monthly_days"
                            render={({ field }) => (
                              <MultiSelectDropdown
                                defaultText={t('DisplayTimingGroup.monthly_day_select_placeholder')}
                                options={MONTHLY_DAY_OPTIONS}
                                selectedValues={field.value}
                                onChange={field.onChange}
                                isDisabled={!monthlyDayEnabled}
                              />
                            )}
                          />
                        </div>
                        <span>日</span>
                        <Form.Select
                          className="text-center"
                          {...register('monthly_day_condition')}
                          disabled={!monthlyDayEnabled}
                          style={{ width: '100px' }}
                        >
                          {CONDITION_SOURCE.map(({ value, labelKey }) => (
                            <option key={value} value={value}>
                              {t(labelKey)}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                      {errors?.monthly_days && (
                        <span className="small text-danger d-inline-block mt-1" style={{ marginLeft: '140px' }}>
                          {errors.monthly_days.message}
                        </span>
                      )}
                      <div className="mt-3 small p-2 d-inline-flex" style={{ background: '#dae8fc' }}>
                        {t('DisplayTimingGroup.date_control_note')}
                      </div>
                    </div>
                  </div>
                  {/* box 3 */}
                  <div className="display-timing-group-box mb-3">
                    <div className="header">
                      <span className="icon bg-primary text-white">3</span>
                      <span>{t('DisplayTimingGroup.time_control')}</span>
                    </div>
                    <div className="body">
                      <Controller
                        control={control}
                        name="given_time_type"
                        render={({ field }) => (
                          <>
                            <Form.Check
                              type="radio"
                              id="given_time_type_none"
                              label={t('DisplayTimingGroup.given_time_none')}
                              checked={field.value === GIVEN_TIME_TYPE_NONE}
                              onChange={() => field.onChange(GIVEN_TIME_TYPE_NONE)}
                            />
                            <div className="d-flex gap-3 align-items-center mt-3">
                              <Form.Check
                                type="radio"
                                id="given_time_type_lunch"
                                label={t('DisplayTimingGroup.given_time_lunch')}
                                checked={field.value === GIVEN_TIME_TYPE_LUNCH}
                                onChange={() => field.onChange(GIVEN_TIME_TYPE_LUNCH)}
                                style={{ width: '140px' }}
                              />
                              <Form.Select
                                className="text-center"
                                {...register('given_lunch_condition')}
                                disabled={field.value !== GIVEN_TIME_TYPE_LUNCH}
                                style={{ width: '100px' }}
                              >
                                {CONDITION_SOURCE.map(({ value, labelKey }) => (
                                  <option key={value} value={value}>
                                    {t(labelKey)}
                                  </option>
                                ))}
                              </Form.Select>
                              <div
                                className={clsx('gap-3 align-items-center', {
                                  'd-flex': givenLunchCondition === CONDITION_INCLUDE,
                                  'd-none': givenLunchCondition === CONDITION_EXCLUDE,
                                })}
                              >
                                <div
                                  className="text-center border rounded py-2"
                                  style={{ width: '86px', background: '#dae8fc' }}
                                >
                                  {t('DisplayTimingGroup.extend_time')}
                                </div>
                                <Form.Select
                                  className="text-center"
                                  {...register('given_lunch_extend_time', { valueAsNumber: true })}
                                  disabled={field.value !== GIVEN_TIME_TYPE_LUNCH}
                                  style={{ width: '80px' }}
                                >
                                  {GIVEN_EXTEND_TIME_OPTIONS.map((minute) => (
                                    <option key={minute} value={minute}>
                                      {minute}分
                                    </option>
                                  ))}
                                </Form.Select>
                              </div>
                            </div>
                            <div className="d-flex gap-3 mt-3">
                              <div className="mt-2">
                                <Form.Check
                                  type="radio"
                                  id="given_time_type_range"
                                  label={t('DisplayTimingGroup.given_time_range')}
                                  checked={field.value === GIVEN_TIME_TYPE_RANGE}
                                  onChange={() => field.onChange(GIVEN_TIME_TYPE_RANGE)}
                                  style={{ width: '140px' }}
                                />
                              </div>
                              <div className="d-flex flex-column">
                                <div className="d-flex align-items-center gap-3">
                                  <TimeRangeControl
                                    control={control}
                                    name="given_range_date"
                                    startFieldName="given_range_start"
                                    endFieldName="given_range_end"
                                    disabled={field.value !== GIVEN_TIME_TYPE_RANGE}
                                  />
                                  <Form.Select
                                    className="text-center"
                                    {...register('given_range_condition')}
                                    disabled={field.value !== GIVEN_TIME_TYPE_RANGE}
                                    style={{ width: '100px' }}
                                  >
                                    {CONDITION_SOURCE.map(({ value, labelKey }) => (
                                      <option key={value} value={value}>
                                        {t(labelKey)}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </div>
                                {(errors?.given_range_date?.given_range_start ||
                                  errors?.given_range_date?.given_range_end) && (
                                  <span className="small text-danger d-inline-block mt-1">
                                    {errors?.given_range_date?.given_range_start?.message ||
                                      errors?.given_range_date?.given_range_end?.message}
                                  </span>
                                )}
                                <div
                                  className={clsx('gap-3 align-items-center mt-3', {
                                    'd-flex': givenRangeCondition === CONDITION_INCLUDE,
                                    'd-none': givenRangeCondition === CONDITION_EXCLUDE,
                                  })}
                                >
                                  <div
                                    className="text-center border rounded py-2"
                                    style={{ width: '86px', background: '#dae8fc' }}
                                  >
                                    {t('DisplayTimingGroup.extend_time')}
                                  </div>
                                  <Form.Select
                                    className="text-center"
                                    {...register('given_range_extend_time', { valueAsNumber: true })}
                                    disabled={field.value !== GIVEN_TIME_TYPE_RANGE}
                                    style={{ width: '80px' }}
                                  >
                                    {GIVEN_EXTEND_TIME_OPTIONS.map((minute) => (
                                      <option key={minute} value={minute}>
                                        {minute}分
                                      </option>
                                    ))}
                                  </Form.Select>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      />
                      <div className="mt-3 small p-2 d-inline-flex" style={{ background: '#dae8fc' }}>
                        {t('DisplayTimingGroup.given_time_control_note')}
                      </div>
                    </div>
                  </div>
                  {/* end box 3 */}
                  <div className="small p-2 d-inline-flex bg-yellow">
                    ※
                    時間指定では開始－OPEN／終了－CLOSEを選択可。終了後も猶予時間内はカート投入済みメニューの注文送信が可能（デフォルト10分）。
                  </div>
                </td>
              </tr>
              <tr>
                <th colSpan={2} className="text-center">
                  {t('DisplayTimingGroup.overlay_display')}
                </th>
                <td>
                  <div className="py-2">
                    <Form.Check
                      type="checkbox"
                      id="overlay_enabled"
                      label={t('DisplayTimingGroup.overlay_display_label')}
                      {...register('overlay_enabled')}
                    />
                  </div>
                  <div className="small mt-3 p-2 bg-yellow">
                    <div>{t('DisplayTimingGroup.overlay_display_note1')}</div>
                    <div>{t('DisplayTimingGroup.overlay_display_note2')}</div>
                  </div>
                </td>
              </tr>
              <tr>
                <th className="text-center align-middle" rowSpan={6} style={{ width: '50%' }}>
                  {t('DisplayTimingGroup.overlay_message_label')}
                  <br />
                  {t('DisplayTimingGroup.overlay_message_label_default')}
                </th>
                <th className="text-center fw-bold" style={{ width: '50%' }}>
                  日本語
                  {overlayEnabled && (
                    <>
                      &nbsp;<span className="text-danger">(*)</span>
                    </>
                  )}
                </th>
                <td>
                  <Form.Control {...register('overlay_message_ja')} disabled={!overlayEnabled}></Form.Control>
                  {overlayEnabled && errors?.overlay_message_ja && (
                    <span className="small text-danger d-inline-block mt-1">{errors.overlay_message_ja.message}</span>
                  )}
                </td>
              </tr>
              <tr>
                <th className="text-center fw-bold">英語</th>
                <td>
                  <Form.Control {...register('overlay_message_en')} disabled={!overlayEnabled}></Form.Control>
                </td>
              </tr>
              <tr>
                <th className="text-center fw-bold">中国語(簡体字)</th>
                <td>
                  <Form.Control {...register('overlay_message_zh_hans')} disabled={!overlayEnabled}></Form.Control>
                </td>
              </tr>
              <tr>
                <th className="text-center fw-bold">中国語(繁体字)</th>
                <td>
                  <Form.Control {...register('overlay_message_zh_hant')} disabled={!overlayEnabled}></Form.Control>
                </td>
              </tr>
              <tr>
                <th className="text-center fw-bold">韓国語</th>
                <td>
                  <Form.Control {...register('overlay_message_ko')} disabled={!overlayEnabled}></Form.Control>
                </td>
              </tr>
              <tr>
                <th className="text-center fw-bold">ベトナム語</th>
                <td>
                  <Form.Control {...register('overlay_message_vi')} disabled={!overlayEnabled}></Form.Control>
                </td>
              </tr>
            </tbody>
          </Table>
        </form>
      </Modal.Body>
    </Modal>
  );
}

export default DisplayTimingGroupDetailModal;
